// DownloadAppBar template2（supergol）：复刻 supergol.today 首页顶部 PWA 安装条。
// 行为与 template1 逐字相同（后台 enableDownloadAppFloatingOnHomeTop 开关 / 仅首页 / App·PWA 内不显示 /
// 本会话关闭标记 / downloadAppBarChange 事件 / 下载激励活动优先弹活动窗），只是版式换成竞品：
//   橙→桃渐变条 3.125rem 高；左 App 图标；两行白字文案（后台 appDownloadTitle，空则回落 i18n pwa.sg.title）；
//   金币装饰图；白底橙字「Instalar」按钮；最右半透明 ✕。
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import utils from "@/utils";
import {
  isDownloadAppBarSessionClosed,
  markDownloadAppBarSessionClosed,
} from "@/utils/downloadAppBarSession";
import { ROUTES } from "@/types/routes";
import type { DownloadAppBarProps } from "../../index.types";
import { setGlobalValue } from "@/stores/GlobalState";
import { useUserDataFlattened } from "@/modules/user";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  usePlatformLogo,
} from "@/modules/homeDisplay/hooks";
import { useT } from "@/i18n/hooks";
import "./DownloadAppBar.scss";
import { AppDownloadDialog } from "@/components/AppDownloadDialog";
import {
  isAppDownloadActivityPromoActive,
  triggerAppDownloadDialog,
} from "@/utils/appDownloadTrigger";
import { OptimizedImage } from "@/components/Base";
// 竞品文案右侧的礼物/奖品装饰：复用仓库既有的金币堆素材（template1 里已注释未用的那张）
import defaultGiftImg from "@/assets/supergol/pwa-prize.png";
import { SkinIcon } from "@/components/SkinIcon";

export const DownloadAppBar: React.FC<DownloadAppBarProps> = ({
  className = "",
}) => {
  const t = useT();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const platformLogo = usePlatformLogo();
  const [topNavShow, setTopNavShow] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useUserDataFlattened();

  // 从 sessionStorage 读取本会话内是否已关闭，默认为显示
  const getInitialVisible = () => {
    try {
      return !isDownloadAppBarSessionClosed();
    } catch {
      return true;
    }
  };

  const [isVisible, setIsVisible] = useState(getInitialVisible);

  // 同步最终的显示状态到全局状态
  useEffect(() => {
    setGlobalValue("downloadAppBarVisible", isVisible);
  }, [isVisible]);

  // 控制显示逻辑（需配置 enableDownloadAppFloatingOnHomeTop 为 true 才在首页顶部显示）
  useEffect(() => {
    // null 表示配置还在加载中，等待，不做任何副作用
    if (enableFloatingOnHomeTop === null) return;

    if (enableFloatingOnHomeTop === false) {
      setTopNavShow(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    const isHomePage =
      location.pathname === ROUTES.HOME ||
      location.pathname === ROUTES.HOME_ALT;

    // 如果不是首页，不显示
    if (!isHomePage) {
      setTopNavShow(false);
      return;
    }

    // 如果已安装APP（在APP内部），不显示，并且设置全局状态为 false
    if (utils.isApp() || utils.updateCheckIsPwa()) {
      setTopNavShow(false);
      setIsVisible(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    setTopNavShow(true);
  }, [location.pathname, isVisible, enableFloatingOnHomeTop]);

  const actualVisible = enableFloatingOnHomeTop && isVisible && topNavShow;
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("downloadAppBarChange", { detail: actualVisible })
    );
  }, [actualVisible]);

  // 关闭bar
  const handleClose = () => {
    setIsVisible(false);
    markDownloadAppBarSessionClosed();
    // 直接派发事件，不依赖 actualVisible effect（组件卸载前确保 Header 能收到）
    window.dispatchEvent(
      new CustomEvent("downloadAppBarChange", { detail: false })
    );
    if (isAuthenticated) {
      setGlobalValue("downloadAppBarVisible", false);
    }
  };

  // 安装：下载激励活动生效且未领取 → 居中活动弹窗；否则本地旧下载弹窗（与 template1 的下载按钮同逻辑）
  const handleInstall = () => {
    if (isAppDownloadActivityPromoActive()) {
      triggerAppDownloadDialog();
      return;
    }
    setDialogVisible(true);
  };

  // 配置加载中，静默等待（组件保持在 React 树中，store 变化后能自动响应）
  if (enableFloatingOnHomeTop === null) {
    return null;
  }

  // 后台明确关闭顶部下载条时不显示
  if (enableFloatingOnHomeTop === false) {
    return null;
  }

  // 如果被用户关闭，不显示
  if (!isVisible) {
    return null;
  }

  // 如果不符合显示条件，不显示
  if (!topNavShow) {
    return null;
  }

  // 文案数据源不变：后台 platformLogo.appDownloadTitle；没配时回落竞品原文
  const noticeText =
    platformLogo?.appDownloadTitle?.trim() || t("pwa.sg.title");
  // 礼物装饰图：后台「App 下载配置」可配（platformLogo.appDownloadBarGiftImage），没配用内置 supergol 原图
  const giftSrc = platformLogo?.appDownloadBarGiftImage
    ? utils.getImageUrl(platformLogo.appDownloadBarGiftImage)
    : defaultGiftImg;

  return (
    <>
      <div className={`downloadAppBar downloadAppBar--sg ${className}`}>
        {/* 左：App 图标 */}
        <OptimizedImage
          className="downloadAppBar__icon"
          src={utils.getImageUrl(window._config?.icon || "")}
          alt="App Icon"
          loading="eager"
          quality="medium"
        />

        {/* 中：两行文案 */}
        <div className="downloadAppBar__title">{noticeText}</div>

        {/* 文案右侧的金币装饰 */}
        <img
          className="downloadAppBar__gift"
          src={giftSrc}
          alt=""
          aria-hidden="true"
          draggable={false}
        />

        {/* 右：安装按钮 */}
        <button
          type="button"
          className="downloadAppBar__install"
          onClick={handleInstall}
        >
          {t("pwa.sg.install")}
        </button>

        {/* 最右：关闭 */}
        <button
          type="button"
          className="downloadAppBar__closeBtn"
          onClick={handleClose}
          aria-label="Close"
        >
          <SkinIcon
            name="downloadBar.close"
            className="downloadAppBar__closeIcon"
          />
        </button>
      </div>

      <AppDownloadDialog
        visible={dialogVisible}
        onClose={() => setDialogVisible(false)}
      />
    </>
  );
};

export default DownloadAppBar;
